import { cva, type VariantProps } from 'class-variance-authority';

import { DARK_MODAL_PANEL } from '../../styles/elevation';

/**
 * Dialog visual variants — mirror Figma "Flashka Design System" file
 * `YZK9r3wDhvw1pY60KlVEIj`, "Modals" page (Figma component name: "Modal") (4:24), component set inside
 * section 4923:23252. Reference variant nodes (read via MCP):
 *   - 4923:37928 – Default (400px) / Header=None / Footer=Stacked Vertically
 *   - 4923:37993 – Default (400px) / Footer=None      (canonical surface)
 *   - 4923:37912 – Default (400px) / Header=Icon
 *   - 4923:37929 – Default (400px) / Header=Back button
 *   - 4923:38200 – Medium (480px)  / Body=Image +Text (image block spec)
 *   - 4923:38080 – Medium (480px)  (48px footer buttons)
 *   - 4923:38250 – Large (560px)
 *   - 5048:15052 – Full size (960PX)
 *
 * Axes (Figma property → prop):
 *   Size:        Default (400px) | Medium (480px) | Large (560px) |
 *                XL (640px) | Full size (960PX)     → size sm | md | lg | xl | full
 *   Alignment:   Left alignment | Center Aligned    → align left | center
 *   Header slot: None | Icon | Back button | Image | Circular small image |
 *                Circular image /icon +back         → headerIcon / onBack /
 *                                                     headerSlot / headerMedia
 *   Footer style: None | Single button | Stacked Vertically (= right-aligned
 *                row) | Stacked Horizontally (= full-width column) |
 *                Stacked Apart                      → footer + footerLayout
 *   Sub heading: boolean                            → subheading
 *
 * Surface: translucent secondary-100/70 glass fill (Figma token
 * `brand/secondary/130` = rgba(245,245,245,0.7)) over a 27px backdrop blur
 * (Figma "Glass" effect radius 27 — `web:` only here; iOS draws the same frost
 * through `DialogGlassBackground.native`, Android keeps the translucent
 * fill, same degradation as Button/Tooltip), 1px secondary-200 border and the
 * standard 0/2/8 @ 10% drop shadow (web class here; native uses
 * `DIALOG_NATIVE_SHADOW`). Radius follows the panel width: 40px on the two
 * small sizes (sm 400 / md 480, per 4923:37928), 48px from lg (560) up.
 *
 * Dark mode is automatic: the palette auto-inverts via CSS vars on both
 * platforms, so no `dark:` classes here (see Button.variants.ts).
 */
export const dialogPanelVariants = cva(
  'relative w-full web:min-w-min flex-col gap-5 border border-secondary-200 ' +
    'bg-surface-overlay/70 web:backdrop-blur-[27px] p-6',
  {
    variants: {
      // Figma fixed widths stay MAX-widths so the panel still shrinks on small
      // screens (the platform wrappers add the viewport margin). Growth comes
      // from the base's `web:min-w-min` (min-width: min-content), which
      // outranks max-width only when the content genuinely cannot lay out any
      // narrower — a row of fixed-width tiles widens the panel instead of
      // overflowing it, while prose is unaffected (a paragraph's min-content is
      // its longest word). Web-only: RN has no `min-content`. Together with the
      // base's `w-full` the max-width doubles as the min-width: the panel is
      // exactly the size's width whenever the viewport allows it.
      //
      // Each size also enforces the DS MIN-HEIGHT — the min-sizes are
      // 400×255 / 480×306 / 560×360 / 640×400 / 960×612. Spare height flows
      // into the body (`DialogBody` fills by default); a dialog that should
      // hug its content instead passes `hug` on `Dialog`, which drops the
      // floor (see DialogSurface).
      size: {
        sm: 'max-w-[400px] min-h-[255px] rounded-[40px]',
        md: 'max-w-[480px] min-h-[306px] rounded-[40px]',
        lg: 'max-w-[560px] min-h-[360px] rounded-[48px]',
        // ponytail: 640 per the DS size list; no Figma variant was reachable
        // via MCP, geometry follows the Large family (48px radius/buttons).
        xl: 'max-w-[640px] min-h-[400px] rounded-[48px]',
        full: 'max-w-[960px] min-h-[612px] rounded-[48px]',
      },
    },
    defaultVariants: { size: 'sm' },
  },
);

/**
 * Body container — the Figma "body container" slot (sheet 5049:16981, layer
 * I…;5048:15226;5049:17189): a SOLID secondary-50 surface with a 1px
 * secondary-200 border, 40px radius and 24px padding. Rich dialog interiors
 * (forms, grids, lists, previews) sit inside it so their content reads
 * against a solid background instead of the translucent glass panel. Figma's
 * border var resolves to #f2f2f2, which has no shared token — secondary-200
 * (#e5e5e5) is the established mapping (new-deck options step). Footer
 * actions always stay OUTSIDE the container.
 *
 * `surface` picks the container FILL:
 *   - `solid`  — the Figma default, flat secondary-50.
 *   - `canvas` — the app's dotted grid (10px pitch, secondary-100 dots) over
 *     that same fill, for interiors that read as a workspace rather than a
 *     form: diagrams, mind maps, card/graph previews. The dot colour is a
 *     palette token, so it auto-inverts in dark like everything else here.
 *     `web:` only — RN has no CSS background-image, so native degrades to
 *     `solid` (same convention as the panel's backdrop blur).
 */
/**
 * The dotted workspace fill — a 10px grid of secondary-100 dots. Shared by the
 * `canvas` body surface and by any other DS dialog surface that should read as
 * a workspace rather than a form (e.g. a `DialogMedia` framing artwork).
 * `web:` only — RN has no CSS background-image, so native degrades to the flat
 * fill (same convention as the panel's backdrop blur).
 */
export const DIALOG_CANVAS_DOTS_CLASS =
  'web:[background-image:radial-gradient(rgb(var(--secondary-100-rgb))_1.5px,transparent_0px)] ' +
  'web:[background-size:10px_10px]';

/*
 * Dark: the container is the modal's CONTENT step (elevation.ts
 * `DARK_MODAL_PANEL`) — a step darker than the glass shell, and the controls
 * on it are the lightest thing in the modal. Only a modal with a THIRD layer
 * (a preview, a drop zone) goes darker again, in that inner element
 * (`DARK_MODAL_WELL`). Light keeps its white body.
 */
export const dialogBodyContainerVariants = cva(
  `w-full rounded-[40px] border border-secondary-200 bg-secondary-50 ${DARK_MODAL_PANEL} p-6`,
  {
    variants: {
      surface: {
        solid: '',
        canvas: DIALOG_CANVAS_DOTS_CLASS,
      },
    },
    defaultVariants: { surface: 'solid' },
  },
);

export type DialogBodySurface = NonNullable<
  VariantProps<typeof dialogBodyContainerVariants>['surface']
>;

/**
 * Web drop shadow — kept out of the CVA base because NativeWind can't apply
 * CSS box-shadows on native; Dialog.native.tsx uses `DIALOG_NATIVE_SHADOW`.
 */
export const DIALOG_WEB_SHADOW_CLASS = 'shadow-[0px_2px_8px_0px_rgba(0,0,0,0.1)]';

/** Native twin of the 0/2/8 @ 10% drop (same split as OVERLAY_NATIVE_SHADOW). */
export const DIALOG_NATIVE_SHADOW = {
  shadowColor: '#000000',
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.1,
  shadowRadius: 8,
  elevation: 6,
} as const;

/**
 * Title — DS `h1` (24/32) in Bold, black (auto-inverts via -950). One size on
 * every panel width — the Figma Modal component draws it as H1 on every size
 * (e.g. 866:18669, 866:18323 in the Toasts/Fixes file).
 */
export const dialogTitleVariants = cva(
  'text-h1 font-bold native:font-mona-bold text-secondary-950',
  {
    variants: {
      align: { left: '', center: 'text-center' },
    },
    defaultVariants: { align: 'left' },
  },
);

/**
 * Subheading + body text — Figma B1: Mona Sans Medium 16/24, secondary-600.
 * `whitespace-pre-line` so a `\n` inside a translated string breaks the line
 * where the translator put it (several confirm dialogs split a question from
 * its consequence that way).
 */
export const dialogBodyTextVariants = cva(
  'text-base font-medium leading-6 native:font-mona-medium text-secondary-600 whitespace-pre-line',
  {
    variants: {
      align: { left: '', center: 'text-center' },
    },
    defaultVariants: { align: 'left' },
  },
);

/**
 * Footer row — 8px gap in every arrangement. Figma's arrangement names map
 * to flex layouts (decoded from the variant symbols' child geometry):
 *   "Stacked Vertically"   → `end`     (right-aligned row of hug buttons)
 *   "Stacked Apart"        → `between` (hug buttons pushed to both edges)
 *   "Stacked Horizontally" → `stack`   (full-width buttons in a column)
 *   Center-aligned pair    → `split`   (row, each button stretched equally —
 *                                       DialogSurface wraps children in flex-1)
 *   "Single button"        → pass one Button (fullWidth on sm–lg; the Full
 *                            size centers a hug button per 5048:15052 —
 *                            use layout `center`).
 */
/*
 * `mt-auto`: the panel carries a per-size min-height, so a short dialog has
 * slack. Without this the slack piles up BELOW the footer and the buttons
 * float mid-panel; the auto margin hands that space to the gap above the
 * footer instead, keeping it on the bottom edge as in Figma. It resolves to 0
 * whenever the content already fills the panel, so the normal 20px panel gap
 * is unaffected.
 */
export const dialogFooterVariants = cva('w-full gap-2 mt-auto', {
  variants: {
    layout: {
      end: 'flex-row items-center justify-end',
      center: 'flex-row items-center justify-center',
      between: 'flex-row items-center justify-between',
      split: 'flex-row items-center',
      stack: 'flex-col items-stretch',
    },
  },
  defaultVariants: { layout: 'end' },
});

/**
 * Title-row controls (close X, back arrow, header-right slot) — the in-flow
 * twin of `DialogTopRight`'s `align="title"`.
 *
 * The DS icon button is 36px tall; the H1 title line box is 32px. Figma's
 * dialog header is exactly the title group's height, with the 36px close
 * button overhanging it. So the control cluster must contribute only the
 * title's 32px to the row: `-my-0.5` trims 2px off each end, which both
 * centres the 20px glyph on the title's FIRST line and keeps every gap below
 * the title (subheading 4px, body 16px) exact instead of inheriting the
 * button's 4px of slack.
 */
export const DIALOG_TITLE_CONTROLS_CLASS = '-my-0.5 shrink-0 flex-row items-center';

/**
 * Header icon chip — Figma Header slot=Icon (node 5216:20558): 44px square,
 * secondary-200 fill, 16px radius, 20px icon centered inside.
 */
export const DIALOG_HEADER_ICON_CHIP_CLASS =
  'h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-secondary-200';

/** Icon size inside the header chip (Figma 20px). */
export const DIALOG_HEADER_ICON_SIZE = 20;

/**
 * Header media block — Figma Header slot=Image / Body=Image +Text image
 * spec (node 4923:27696): full-width, 24px radius, secondary-200 border,
 * clipped.
 */
export const DIALOG_HEADER_MEDIA_CLASS =
  'w-full overflow-hidden rounded-3xl border border-secondary-200';

/**
 * Numeric twin of the per-size `max-w-[…]` classes — keep in sync with
 * `dialogPanelVariants`. Native code (which can't introspect Tailwind
 * classes) uses it to size the tap-swallowing wrapper around the panel.
 */
/**
 * Panel corner radius in px per size — the numeric twin of the CVA's
 * per-size `rounded-[…]`, keep the two in sync. Native needs the number: RN
 * can't read a Tailwind class, and the glass BlurView has to clip itself to
 * the panel shape.
 */
export const DIALOG_PANEL_RADIUS_PX = {
  sm: 40,
  md: 40,
  lg: 48,
  xl: 48,
  full: 48,
} as const;

export const DIALOG_MAX_WIDTH_PX = {
  sm: 400,
  md: 480,
  lg: 560,
  xl: 640,
  full: 960,
} as const;

/**
 * Tablet upgrade map — on native, a dialog steps up ONE DS size when the
 * window is tablet-sized (`!useBreakpoint().isPhone`, i.e. width ≥ 768:
 * iPads, Android tablets, either orientation). The DS widths were picked
 * against phone-ish panels; on a tablet the same `size` reads undersized.
 * Width-based, not device-based, so iPad split-view narrower than a phone
 * keeps the phone size. Web is untouched — desktop already renders the DS
 * widths as designed.
 */
export const DIALOG_TABLET_SIZE_UP = {
  sm: 'md',
  md: 'lg',
  lg: 'xl',
  xl: 'full',
  full: 'full',
} as const satisfies Record<
  NonNullable<DialogPanelVariantProps['size']>,
  NonNullable<DialogPanelVariantProps['size']>
>;

export type DialogPanelVariantProps = VariantProps<typeof dialogPanelVariants>;
export type DialogFooterVariantProps = VariantProps<typeof dialogFooterVariants>;
